import { Head, Link } from "@inertiajs/react"
import PublicLayout from "@/components/public/layout"
import { ScrollReveal } from "@/components/ui/scroll-reveal"
import { VideoPhone } from "@/components/public/video-phone"
import { BreadcrumbSchema } from "@/components/public/breadcrumb-schema"
import { RelatedPages } from "@/components/public/related-pages"
import { relatedFor } from "@/data/related-pages"
import type { SeoMeta } from "@/types"

/* ------------------------------------------------------------------ */
/*  Data                                                               */
/* ------------------------------------------------------------------ */

const ADVANTAGES = [
  {
    title: "Compete with corporate marketing budgets",
    description:
      "Purplebricks spends millions on marketing. Connells has a dedicated content team. You have you. QuiteRight levels the playing field by giving you professional video marketing at a fraction of the cost, so your listings look just as polished as the nationals.",
  },
  {
    title: "Stand out at market appraisals",
    description:
      "Walk into a market appraisal with a video already made for the vendor's property. While the corporate agent is promising \"we'll sort the marketing later\", you're showing them exactly what their listing will look like on Instagram. That's how independents win instructions.",
  },
  {
    title: "Build your local brand on social media",
    description:
      "Every video carries your logo, your colours, your name. When locals see your branded property videos across Instagram, Facebook, and TikTok, you become the agent they think of first. Consistent video output builds brand awareness faster than any flyer drop.",
  },
  {
    title: "No marketing team required",
    description:
      "You don't need a social media manager, a videographer, or an editing suite. Paste a listing URL, pick a template, and publish. One person can create professional video content for every listing in your portfolio.",
  },
]

const FEATURES = [
  {
    title: "60-second video creation",
    description: "Paste a listing URL from Rightmove, Zoopla, or OnTheMarket. Get a professional, branded video ad in about a minute.",
  },
  {
    title: "Your brand, every time",
    description: "Upload your logo once. Every video features your branding, colours, and contact details consistently across all listings.",
  },
  {
    title: "All social platforms covered",
    description: "Instagram Reels, TikTok, YouTube Shorts, Facebook. Every video comes in all formats, no re-editing needed.",
  },
  {
    title: "AI voiceover and scripts",
    description: "Professional scripts generated from your listing details with AI voiceover. Multiple voices to match your brand personality.",
  },
  {
    title: "Market appraisal tool",
    description: "Create a video before you've even won the instruction. Show vendors exactly what their marketing will look like with your agency.",
  },
  {
    title: "Affordable at any scale",
    description: "From \u00A349/month for self-service to \u00A3299/month for 50 managed videos. Scales with your business without scaling your costs.",
  },
]

type FaqItem = { q: string; a: string }

const FAQ: FaqItem[] = [
  {
    q: "How can an independent agent afford video marketing?",
    a: "That's exactly the problem QuiteRight solves. Traditional video production costs \u00A3300\u2013\u00A3500 per property. QuiteRight's Managed plan covers 50 videos per month for \u00A3299, which is \u00A35.98 per video. Even the self-service plan at \u00A349/month puts professional video within reach of any single-branch independent.",
  },
  {
    q: "Do I need technical skills to use QuiteRight?",
    a: "None at all. If you can paste a URL, you can create a video. QuiteRight handles the script, voiceover, music, branding, and formatting automatically. The whole process takes about 60 seconds per listing.",
  },
  {
    q: "Can I use QuiteRight for market appraisals?",
    a: "Yes, and this is where independents find the most value. Create a video for a property before the market appraisal meeting. Show the vendor exactly what their listing will look like on social media. It's a powerful differentiator against corporate agents who can only promise marketing after the instruction is signed.",
  },
  {
    q: "How does QuiteRight help me compete with bigger agencies?",
    a: "Corporate agencies have dedicated marketing teams, video editors, and content budgets. QuiteRight gives you the same output: professional, branded property video ads across every social platform. The difference is you can do it yourself in 60 seconds instead of waiting for a marketing department.",
  },
  {
    q: "What if I only have a few listings at a time?",
    a: "The self-service plan at \u00A349/month is designed for exactly this. Create videos as you need them, with no minimum volume. Even 3\u20135 videos a month gives you a significant marketing advantage over agents with no video presence at all.",
  },
]

/* ------------------------------------------------------------------ */
/*  Page                                                               */
/* ------------------------------------------------------------------ */

export default function ForIndependentAgents({ meta }: { meta: SeoMeta }) {
  return (
    <PublicLayout>
      <Head>
        <title>{meta.title}</title>
        <meta name="description" content={meta.description} />
        <link rel="canonical" href={meta.canonical} />

        <meta property="og:title" content={meta.title} />
        <meta property="og:description" content={meta.description} />
        <meta property="og:url" content={meta.canonical} />
        <meta property="og:type" content="website" />
        <meta property="og:image" content={meta.image} />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={meta.title} />
        <meta name="twitter:description" content={meta.description} />
        <meta name="twitter:image" content={meta.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebPage",
            name: meta.title,
            description: meta.description,
            url: meta.canonical,
            publisher: {
              "@type": "Organization",
              name: "QuiteRight",
              url: "https://www.quiteright.co.uk",
            },
          })}
        </script>

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FAQ.map((faq) => ({
              "@type": "Question",
              name: faq.q,
              acceptedAnswer: { "@type": "Answer", text: faq.a },
            })),
          })}
        </script>

        <BreadcrumbSchema canonical={meta.canonical} items={[{ name: "For Independent Agents" }]} />
      </Head>

      {/* Hero */}
      <section className="px-6 py-24 md:py-32">
        <div className="mx-auto max-w-6xl">
          <div className="grid items-center gap-12 md:grid-cols-2">
            <div>
              <p className="text-xs font-medium uppercase tracking-[0.2em] text-red">
                For independent agents
              </p>
              <h1 className="mt-4 font-display text-4xl font-bold tracking-tight text-charcoal sm:text-5xl">
                Corporate marketing, independent spirit
                <span className="text-red">.</span>
              </h1>
              <p className="mt-6 max-w-lg text-lg text-slate">
                You don&rsquo;t need a marketing department to look like you have
                one. Professional video for every listing, branded to your agency,
                created in 60 seconds.
              </p>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Link
                  href="/sign-up"
                  className="inline-flex items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90"
                >
                  Start your free trial
                </Link>
                <Link
                  href="/demo"
                  className="inline-flex items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone"
                >
                  Book a demo
                </Link>
              </div>
            </div>
            <div className="relative flex justify-center">
              <VideoPhone />
              <div className="absolute -bottom-4 -left-4 rounded-lg bg-charcoal px-4 py-2.5 shadow-md md:-left-2">
                <p className="text-xs font-medium text-warm-grey">Professional video</p>
                <p className="font-display text-xl font-bold text-white">from &pound;49/mo</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Advantages */}
      <section className="border-t border-stone bg-cream px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              Your advantage
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Why independents are winning with video
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2">
            {ADVANTAGES.map((item, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <p className="font-display text-lg font-bold text-charcoal">{item.title}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate">{item.description}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* Features */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-5xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              What you get
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              A marketing department in your pocket
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <ScrollReveal stagger className="mt-12 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature, i) => (
              <div key={i} className="rounded-xl border border-stone bg-white p-6">
                <p className="font-display text-base font-bold text-charcoal">{feature.title}</p>
                <p className="mt-3 text-sm leading-relaxed text-slate">{feature.description}</p>
              </div>
            ))}
          </ScrollReveal>
        </div>
      </section>

      {/* The maths */}
      <section className="border-t border-stone bg-charcoal px-6 py-20">
        <div className="mx-auto max-w-4xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              The maths
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-white md:text-4xl">
              One extra instruction pays for a year
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12 grid gap-6 sm:grid-cols-3">
            <ScrollReveal>
              <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                  Average instruction fee
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-white">
                  &pound;3,500
                </p>
                <p className="mt-1 text-sm text-warm-grey">based on \u00A3250k sale at 1.4%</p>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="rounded-xl border border-red/30 bg-red/10 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-red">
                  QuiteRight annual cost
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-white">
                  &pound;588
                </p>
                <p className="mt-1 text-sm text-warm-grey">self-service plan, billed annually</p>
              </div>
            </ScrollReveal>
            <ScrollReveal>
              <div className="rounded-xl border border-white/10 bg-white/5 p-6 text-center">
                <p className="text-xs font-medium uppercase tracking-[0.15em] text-warm-grey">
                  ROI from one instruction
                </p>
                <p className="mt-3 font-display text-3xl font-bold text-red">
                  6x
                </p>
                <p className="mt-1 text-sm text-warm-grey">return on investment</p>
              </div>
            </ScrollReveal>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="border-t border-stone px-6 py-20">
        <div className="mx-auto max-w-3xl">
          <ScrollReveal>
            <p className="text-center text-xs font-medium uppercase tracking-[0.2em] text-red">
              FAQ
            </p>
            <h2 className="mt-4 text-center font-display text-3xl font-bold text-charcoal md:text-4xl">
              Common questions
              <span className="text-red">.</span>
            </h2>
          </ScrollReveal>

          <div className="mt-12">
            <ScrollReveal>
              <dl className="divide-y divide-stone">
                {FAQ.map((item, i) => (
                  <details key={i} className="group">
                    <summary className="flex cursor-pointer items-start justify-between gap-4 py-5 text-left [&::-webkit-details-marker]:hidden">
                      <dt className="text-sm font-semibold leading-relaxed text-charcoal">
                        {item.q}
                      </dt>
                      <span className="mt-0.5 flex size-6 shrink-0 items-center justify-center rounded-full bg-stone text-slate transition-transform duration-200 group-open:rotate-45">
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth={2} className="size-3">
                          <path d="M8 3v10M3 8h10" />
                        </svg>
                      </span>
                    </summary>
                    <dd className="pb-5 text-sm leading-relaxed text-slate">{item.a}</dd>
                  </details>
                ))}
              </dl>
            </ScrollReveal>
          </div>
        </div>
      </section>

      <RelatedPages links={relatedFor("for/independent-agents")} />

      {/* CTA */}
      <section className="border-t border-stone bg-cream px-6 py-24">
        <div className="mx-auto max-w-3xl text-center">
          <ScrollReveal>
            <h2 className="font-display text-3xl font-bold text-charcoal md:text-4xl">
              Punch above your weight
              <span className="text-red">.</span>
            </h2>
            <p className="mx-auto mt-4 max-w-xl text-lg text-slate">
              Independent doesn&rsquo;t mean under-resourced. QuiteRight gives you
              the video marketing output of an agency ten times your size.
            </p>
            <div className="mt-8 flex flex-col items-center justify-center gap-4 sm:flex-row">
              <Link
                href="/sign-up"
                className="inline-flex w-full items-center justify-center rounded-md bg-red px-6 py-3 text-base font-medium text-white transition-colors hover:bg-red/90 sm:w-auto"
              >
                Start your free trial
              </Link>
              <Link
                href="/demo"
                className="inline-flex w-full items-center justify-center rounded-md border border-stone bg-white px-6 py-3 text-base font-medium text-charcoal transition-colors hover:bg-stone sm:w-auto"
              >
                Book a demo
              </Link>
            </div>
          </ScrollReveal>
        </div>
      </section>
    </PublicLayout>
  )
}
